import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useSelector} from 'react-redux';

import {Skeleton} from 'primereact/skeleton';
import {
    Dropdown,
    DropdownProps,
    DropdownChangeEvent,
    DropdownFilterEvent
} from 'primereact/dropdown';
import {VirtualScrollerLazyEvent} from 'primereact/virtualscroller';

import {StoreState} from '~/frontend/src/store';
import User from '~/shared/src/model/User';
import Item from '~/shared/src/ci/Item';
import {
    BaseRequestParameters,
    defaultBaseRequestParameters
} from '~/shared/src/utilities/modules/common';
import {
    getDataStructure,
    getInitialSorters,
    getItemAttributeColumnDefinitions
} from '~/shared/src/utilities/modules/finder';
import {
    LoadReferenceFieldItemsParameters,
    LoadSearchItemFieldItemsParameters,
    LoadSearchItemFieldItemsResult
} from '~/shared/src/utilities/modules/form';

import xhr from '~/frontend/src/utilities/xhr';
import {XhrComponentState} from '~/frontend/src/reducers/xhr';
import messageUtils from '~/shared/src/utilities/messageUtils';


type InputProps = Omit<DropdownProps, 'onChange'>;

type DefaultProps = BaseRequestParameters & {

    /**
     * The ItemTypeName to show items for.
     */
    itemTypeName? : string;

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;

    /**
     * The label for the field.
     */
    label? : string;

    /**
     * The CSS class names to style the input field with.
     */
    className? : string;

    /**
     * The styles to style the input field with.
     */
    inputStyle? : React.CSSProperties;
}

const defaultProps : Required<DefaultProps> = {
    ...defaultBaseRequestParameters,

    itemTypeName: '',
    markDirty   : false,
    label       : '',
    className   : '',
    inputStyle  : { width: '100%' }
};

type Props = {
    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The selected status.
     */
    value : Item | undefined;

    /**
     * The value to determine dirty against.
     */
    originalValue? : Item | undefined;

    itemLabel? : string;

    /**
     * Callback to invoke if value has changed.
     *
     * @param {Item} item
     */
    onChange : ( item : Item ) => void;

    style? : React.CSSProperties;

    /**
     * The running requests belonging to this component (uid).
     *
     * Will be mapped from global state.
     */
    xhrRequestsForComponent? : XhrComponentState;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

export type ComponentProps = InputProps & DefaultProps & Props;
type AllProps = InputProps & Required<DefaultProps> & Props;


const SearchItemDropdown = ( props : AllProps ) => {
    const user                                          = useSelector( ( state : StoreState ) => state.session?.user ),
          {
              className,
              inputStyle,
              itemTypeName,
              itemLabel,
              label,
              markDirty,
              onChange,
              originalValue,
              placeholder,
              schema,
              status,
              style,
              timestamp,
              timezone,
              uid,
              value,
              ...dropdownProps
          }                                             = props,
          searchRequestUid                              = 'loadData',
          filterTimeoutId                               = useRef<NodeJS.Timeout | undefined>(),
          requestTimeoutId                              = useRef<NodeJS.Timeout | undefined>(),
          [ searchItems, setSearchItems ]               = useState<Item[]>( Array.from( { length: 101 } ) as Item[] ),
          [ searchItemsLoading, setSearchItemsLoading ] = useState<boolean>( false ),
          filter                                        = useRef<string>( '' ),
          first                                         = useRef<number>( 0 ),
          last                                          = useRef<number>( 0 ),
          [ load, setLoad ]                             = useState<boolean>( false ),
          dropdownClassNames                            = useMemo(
              () => {
                  const retVal = [ 'yn-inputgroup' ];

                  if ( !label ) {
                      retVal.push( className );
                  }
                  if ( markDirty && value !== originalValue ) {
                      retVal.push( 'yn-dirty-border' );
                  }

                  return retVal;
              },
              [
                  className,
                  label,
                  markDirty,
                  originalValue,
                  value
              ]
          ),
          dataStructure                                 = useMemo(
              () => {
                  return user ? getDataStructure( schema, itemTypeName, user ) : undefined;
              },
              [
                  itemTypeName,
                  schema,
                  user
              ]
          ),
          columnDefinitions                             = useMemo(
              () => {
                  return dataStructure ? getItemAttributeColumnDefinitions( dataStructure ) : undefined;
              },
              [
                  dataStructure
              ]
          ),
          options                                       = useMemo(
              () => {
                  const retVal     = [ ...searchItems ],
                        valueIndex = value ? retVal.findIndex( sI => sI?.id === value.id ) : -1;

                  if ( valueIndex === -1 ) {
                      retVal.unshift( value );
                  }

                  return retVal;
              },
              [
                  itemLabel,
                  searchItems,
                  value
              ]
          ),
          dataTemplate                                  = useCallback(
              ( item : Item ) => {
                  let retVal = itemLabel
                      ? messageUtils.getMessageTemplateStringResolver( item )( itemLabel ).trim()
                      : '';

                  if ( !retVal ) {
                      retVal = `${item.key} - ${item.name}`.trim();
                  }

                  return retVal;
              },
              [
                  itemLabel
              ]
          ),
          selectedDataTemplate                          = useCallback(
              ( item : Item ) => {
                  let retVal : string | JSX.Element = <>{placeholder ?? '&nbsp;'}</>;

                  if ( item ) {
                      retVal = itemLabel
                          ? messageUtils.getMessageTemplateStringResolver( item )( itemLabel ).trim()
                          : '';

                      if ( !retVal.trim() ) {
                          retVal = `${item.key} - ${item.name}`.trim();
                      }
                  }

                  return retVal;
              },
              [
                  itemLabel,
                  placeholder
              ]
          ),
          onInputChange                                 = ( event : DropdownChangeEvent ) => {
              if ( value !== event.value ) {
                  onChange( event.value );
              }
          },
          onFilter                                      = useCallback(
              ( event : DropdownFilterEvent ) => {
                  if ( filter.current !== event.filter ) {
                      filter.current = event.filter;

                      if ( filterTimeoutId.current ) {
                          clearTimeout( filterTimeoutId.current );
                      }

                      filterTimeoutId.current = setTimeout(
                          () => {
                              setSearchItems( Array.from( { length: 101 } ) as Item[] );
                              first.current = 0;
                              last.current = 101;
                              setLoad( true );
                          },
                          250
                      );
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          onHide                                        = useCallback(
              () => {
                  setLoad( false );
              },
              [
                  'onlyOnce'
              ]
          ),
          onLazyLoad                                    = useCallback(
              ( event : VirtualScrollerLazyEvent ) => {
                  const eFirst = event.first as number,
                        eLast  = ( event.last as number ) - 1;

                  if ( first.current !== eFirst || last.current !== eLast ) {
                      first.current = eFirst;
                      last.current = eLast;
                      setLoad( true );
                  }
              },
              [
                  'onlyOnce'
              ]
          );

    // load data if
    useEffect(
        () => {
            if ( load && dataStructure && columnDefinitions && itemTypeName && user ) {
                const limit  = last.current > 0 ? last.current - first.current : 100,
                      params = itemLabel
                          ? {
                              schema,

                              itemTypeName,
                              searchString: filter.current,

                              // use shown label for search
                              searchTemplate: itemLabel,

                              start: first.current,
                              limit: limit
                          } as LoadReferenceFieldItemsParameters
                          : {
                              schema,
                              status,
                              timestamp,
                              timezone,

                              itemTypeName,
                              searchString: filter.current,

                              columns: columnDefinitions.map( colDef => colDef.field ),
                              sorters: getInitialSorters( dataStructure, schema, itemTypeName ),

                              start: first.current,
                              limit: limit
                          } as LoadSearchItemFieldItemsParameters;

                let newSearchItems = [ ...searchItems ],
                    allLoaded      = true;

                // check if result window has loaded before
                for ( let i = first.current; i <= last.current, i < searchItems.length; i++ ) {
                    if ( searchItems[ i ] === undefined || searchItems[ i ] === null ) {
                        allLoaded = false;
                        break;
                    }
                }

                if ( !allLoaded ) {
                    const doLoad = async () => {
                        setSearchItemsLoading( true );

                        const response = itemLabel
                            ? await xhr.post(
                                `${BASE_URL}/api/form/loadReferenceFieldItems`,
                                {
                                    ...params,
                                    itemTypeNames: [ params.itemTypeName ]
                                },
                                undefined,
                                uid,
                                searchRequestUid
                            )
                            : await xhr.post(
                                `${BASE_URL}/api/form/loadSearchItemFieldItems`,
                                params,
                                undefined,
                                uid,
                                searchRequestUid
                            );

                        if ( response?.data ) {
                            const responseData = response.data as LoadSearchItemFieldItemsResult;

                            if ( responseData.data ) {
                                const items = ( responseData?.data?.items ?? [] ).map( item => new Item( item, uid, schema, true ) );

                                if ( newSearchItems.length > responseData.metaData.total ) {
                                    newSearchItems.splice( responseData.metaData.total - 1 );
                                }
                                else if ( newSearchItems.length < responseData.metaData.total ) {
                                    newSearchItems = newSearchItems.concat( Array.from( { length: responseData.metaData.total - newSearchItems.length } ) as Item[] );
                                }

                                // sort
                                if ( itemLabel ) {
                                    items.sort( ( item1, item2 ) => {
                                        const label1 = messageUtils.getMessageTemplateStringResolver( item1 )( itemLabel, true ).toLowerCase(),
                                              label2 = messageUtils.getMessageTemplateStringResolver( item2 )( itemLabel, true ).toLowerCase();

                                        if ( label1 > label2 ) {
                                            return 1;
                                        }
                                        else if ( label1 < label2 ) {
                                            return -1;
                                        }
                                        else {
                                            return 0;
                                        }
                                    } );
                                }
                                else {
                                    items.sort( ( item1, item2 ) => {
                                        if ( item1.key > item2.key ) {
                                            return 1;
                                        }
                                        else if ( item1.key < item2.key ) {
                                            return -1;
                                        }
                                        else {
                                            return 0;
                                        }
                                    } );
                                }

                                for ( let i = 0; i < items.length; i++ ) {
                                    newSearchItems[ first.current + i ] = items[ i ];
                                }
                            }

                            setSearchItemsLoading( false );
                        }

                        setSearchItems( newSearchItems );
                    };

                    // abort running load
                    xhr.abort( uid, searchRequestUid );

                    if ( requestTimeoutId.current ) {
                        clearTimeout( requestTimeoutId.current );
                    }

                    requestTimeoutId.current = setTimeout(
                        () => {
                            doLoad().catch( ( error ) => {
                                console.error( error );
                                setSearchItemsLoading( false );
                            } );
                        },
                        250
                    );
                }
                else {
                    setSearchItems( newSearchItems );
                }

                setLoad( false );
            }
        },
        [
            columnDefinitions,
            dataStructure,
            filter.current,
            first.current,
            itemLabel,
            itemTypeName,
            last.current,
            load,
            schema,
            status,
            timestamp,
            timezone,
            user
        ]
    );

    return <div className={className + ' field'} style={style}>
        {label
            ? <label
                htmlFor={`${uid}_statusDropdown`}
                className="block"
            >
                {label}
            </label>
            : ''
        }
        <Dropdown
            inputId={`${uid}_statusDropdown`}

            value={value}

            options={[ ...options ]}
            dataKey="id"
            optionLabel="key"

            onChange={onInputChange}
            valueTemplate={selectedDataTemplate}
            itemTemplate={dataTemplate}

            className={dropdownClassNames.join( ' ' )}
            style={inputStyle}

            filter
            showClear
            onFilter={onFilter}

            onHide={onHide}
            resetFilterOnHide={false}

            virtualScrollerOptions={{
                lazy           : true,
                onLazyLoad     : onLazyLoad,
                itemSize       : 27,
                delay          : 0,
                showLoader     : true,
                loading        : searchItemsLoading,
                loadingTemplate: () => {
                    return (
                        <div className="flex align-items-center p-2" style={{ height: '27px' }}>
                            <Skeleton width={`${30 + Math.round( Math.random() * 60 )}%`} height="1rem"/>
                        </div>
                    )
                }
            }}

            {...dropdownProps}
        />
    </div>;
};

SearchItemDropdown.defaultProps = defaultProps;

export default SearchItemDropdown;
